import { Toaster as Sonner } from "@/components/ui/sonner";
import { ThemeProvider } from "@/components/theme-provider";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { LanguageProvider } from "./contexts/LanguageContext";
import { AuthProvider } from "./contexts/AuthContext";
import { PaddleProvider } from "./components/paddle/PaddleProvider";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { EmergencyFallback } from "./components/EmergencyFallback";
import { lazy, Suspense } from "react";

// Public pages - lazy loaded
const Index = lazy(() => import("./pages/Index"));
const Login = lazy(() => import("./pages/Login"));
const Register = lazy(() => import("./pages/Register"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const SubscriptionSuccess = lazy(() => import("./pages/SubscriptionSuccess"));
const SubscriptionCancel = lazy(() => import("./pages/SubscriptionCancel"));
const PaddleCheckout = lazy(() => import("./pages/PaddleCheckout"));
const TermsOfService = lazy(() => import("./pages/legal/TermsOfService"));
const PrivacyPolicy = lazy(() => import("./pages/legal/PrivacyPolicy"));
const RefundPolicy = lazy(() => import("./pages/legal/RefundPolicy"));
const IntegrationsCallback = lazy(() => import("./pages/IntegrationsCallback"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Landing page - lazy loaded
const Landing = lazy(() => import("./pages/Landing"));

// Health check - NOT lazy (instant load)
import HealthCheck from "./pages/HealthCheck";

// Core app shell - lazy loaded only when accessing /app/*
const AppShell = lazy(() => import("./components/AppShell"));

import "./App.css";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30 * 1000, // 30 seconds - ULTRA FAST refresh
      gcTime: 10 * 60 * 1000, // 10 minutes - Faster cleanup
      retry: 1, // Single retry only
      refetchOnWindowFocus: false,
      refetchOnMount: false, // Don't auto-refetch on mount
      refetchOnReconnect: false, // Don't auto-refetch on reconnect
    },
    mutations: {
      retry: 0, // No retries for mutations - fail fast
    },
  },
});

const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen bg-background">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
      <p className="text-lg text-muted-foreground">Cargando...</p>
    </div>
  </div>
);

function App() {
  console.log("[App] Initializing with route-based code splitting");
  
  return (
    <ErrorBoundary fallback={<EmergencyFallback />}>
      <QueryClientProvider client={queryClient}>
        <LanguageProvider>
          <AuthProvider>
            <PaddleProvider>
              <ThemeProvider>
                <Sonner />
                <BrowserRouter>
              <Routes>
                {/* Landing page */}
                <Route path="/" element={<Suspense fallback={<PageLoader />}><Landing /></Suspense>} />
                
                {/* Health check - smoke test */}
                <Route path="/health" element={<HealthCheck />} />
                
                {/* Public routes - lazy loaded */}
                <Route path="/home" element={<Suspense fallback={<PageLoader />}><Index /></Suspense>} />
                <Route path="/login" element={<Suspense fallback={<PageLoader />}><Login /></Suspense>} />
                <Route path="/register" element={<Suspense fallback={<PageLoader />}><Register /></Suspense>} />
                <Route path="/forgot-password" element={<Suspense fallback={<PageLoader />}><ForgotPassword /></Suspense>} />
                <Route path="/reset-password" element={<Suspense fallback={<PageLoader />}><ResetPassword /></Suspense>} />
                <Route path="/subscription-success" element={<Suspense fallback={<PageLoader />}><SubscriptionSuccess /></Suspense>} />
                <Route path="/subscription-cancel" element={<Suspense fallback={<PageLoader />}><SubscriptionCancel /></Suspense>} />
                <Route path="/paddle/checkout" element={<Suspense fallback={<PageLoader />}><PaddleCheckout /></Suspense>} />
                
                {/* Legal pages */}
                <Route path="/terms-of-service" element={<Suspense fallback={<PageLoader />}><TermsOfService /></Suspense>} />
                <Route path="/privacy-policy" element={<Suspense fallback={<PageLoader />}><PrivacyPolicy /></Suspense>} />
                <Route path="/refund-policy" element={<Suspense fallback={<PageLoader />}><RefundPolicy /></Suspense>} />
                <Route path="/integrations/callback" element={<Suspense fallback={<PageLoader />}><IntegrationsCallback /></Suspense>} />

                {/* Core app - lazy loaded with all providers */}
                <Route 
                  path="/app/*" 
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <AppShell />
                    </Suspense>
                  } 
                />

                {/* 404 */}
                <Route path="*" element={<Suspense fallback={<PageLoader />}><NotFound /></Suspense>} />
              </Routes>
            </BrowserRouter>
          </ThemeProvider>
        </PaddleProvider>
          </AuthProvider>
        </LanguageProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
